import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface Tenant {
  Id: string;
  Description: string;
  TransientFlows: boolean;
  TeamsErrorWebhook: string;
  RequireApprovalForChanges?: boolean;
  AllowSelfApproval?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private api: AxiosInstance;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private authService: KeycloakAuthService, private apiConfig: ApiConfigService) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        config.baseURL = `${baseUrl}/`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async getTenant(): Promise<Tenant | null> {
    try {
      const tenantId = this.apiConfig.getTenantId();
      const response = await this.api.get(`/${tenantId}/tenant`);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getAllTenants(): Promise<Tenant[] | null> {
    try {
      const response = await this.api.get('/tenant');
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateTenant(tenant: Tenant): Promise<AxiosResponse | null> {
    try {
      const tenantId = this.apiConfig.getTenantId();
      const response = await this.api.put(`/${tenantId}/tenant`, tenant);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getTenantById(tenantId: string): Promise<Tenant | null> {
    try {
      const response = await this.api.get(`/${tenantId}/tenant`);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createTenant(tenant: Tenant): Promise<string> {
    try {
      const response = await this.api.post('/tenant', tenant);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateTenantById(tenantId: string, tenant: Tenant): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.put(`/${tenantId}/tenant`, tenant);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteTenant(tenantId: string): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.delete(`/${tenantId}/tenant`);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
